/**
 * ContactPage — MADE-304
 *
 * Public contact / sailmaking inquiry page for offshore-sails.com.
 */

import React from 'react';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { MarketingLayout } from './MarketingLayout';
import { OsContactForm } from './OsContactForm';

export const ContactPage: React.FC = () => {
  return (
    <MarketingLayout pageTitle="Contact Us">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#002680', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography component="h1" variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 1.5 }}>
            Get a Quote
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 500, mx: 'auto', lineHeight: 1.75 }}>
            Tell us about your boat and the sails you need. We will come back to you
            with honest advice and a fair quote, no pressure.
          </Typography>
          <Box sx={{ width: 60, height: 4, bgcolor: '#FFC726', mx: 'auto', mt: 3 }} />
        </Container>
      </Box>

      {/* ── Form + sidebar ──────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: '#fafbff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">

            {/* Sidebar */}
            <Grid item xs={12} md={4} order={{ xs: 2, md: 1 }}>
              <Stack spacing={4}>
                <Box>
                  <Typography variant="h6" sx={{ color: '#002680', fontWeight: 700, mb: 1.5 }}>
                    What to expect
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.8 }}>
                    We read every inquiry personally. If your project is straightforward we
                    can usually turn around a quote within a few days. For larger or more
                    complex builds we may come back with a few clarifying questions first.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ color: '#002680', fontWeight: 700, mb: 1.5 }}>
                    Prefer email?
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon sx={{ color: '#FFC726', fontSize: 20 }} />
                    <Typography
                      component="a"
                      href="mailto:info@offshore-sails.com"
                      variant="body2"
                      sx={{
                        color: '#002680',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      info@offshore-sails.com
                    </Typography>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    bgcolor: '#002680',
                    borderRadius: 2,
                    p: 3,
                    borderLeft: '4px solid #FFC726',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8 }}>
                    We build mainsails, headsails, spinnakers, and code zeros on vessels of
                    all sizes, with particular expertise in large and offshore craft that
                    demand more than a standard loft can deliver.
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Form */}
            <Grid item xs={12} md={8} order={{ xs: 1, md: 2 }}>
              <Box
                sx={{
                  bgcolor: 'white',
                  borderRadius: 3,
                  p: { xs: 3, md: 5 },
                  border: '1px solid rgba(0,38,128,0.10)',
                  boxShadow: '0 4px 24px rgba(0,38,128,0.07)',
                }}
              >
                <OsContactForm />
              </Box>
            </Grid>

          </Grid>
        </Container>
      </Box>

    </MarketingLayout>
  );
};
